import SprigSDK, {
  SprigEvent,
} from '@sprig-technologies/sprig-browser/dist/index.js';
import {LocalStorage} from '@stripe-internal/stripe-cookies';

import {shouldTrackEvent} from './helpers';
import type {AppConfig} from '../initialize';
import {initialize} from '../initialize';
import {extractCSPNonce} from '../../shared/extractCspNonce';

let Sprig: ReturnType<(typeof SprigSDK)['configure']> | null = null;

type SprigSurveyHeightPayload = {
  name: 'survey.height';
  contentFrameHeight: number;
};

type SprigSurveyWillClosePayload = {
  name: 'survey.willClose';
  initiator: 'survey.completed' | 'close.click';
};

type SprigSurveyWillPresentPayload = {
  name: SprigEvent.SurveyWillPresent;
  'survey.id': number;
};

function isSprigSurveyWillPresentPayload(
  payload: unknown,
): payload is SprigSurveyWillPresentPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'name' in payload &&
    payload.name === SprigEvent.SurveyWillPresent &&
    'survey.id' in payload &&
    typeof payload['survey.id'] === 'number'
  );
}

const storage = new LocalStorage();

// Sprig's private _API_URL config expects an origin. Consumers may pass a
// full URL, so strip path/query before Sprig appends its own SDK routes.
const normalizeSprigApiURL = (sprigApiURL: string): string | undefined => {
  try {
    return new URL(sprigApiURL).origin;
  } catch {
    return undefined;
  }
};

/**
 * Patches navigator.clipboard methods to gracefully handle NotAllowedError
 * in Safari. Safari throws this error when the Clipboard API is called outside
 * of a direct user gesture or within a sandboxed iframe (like Sprig's double-iframe
 * survey context). Since clipboard access is non-essential for survey functionality,
 * we suppress the error and return gracefully.
 *
 * See: https://jira.corp.stripe.com/browse/RUN_DASHPROD-5250
 */
export const patchClipboardForSafari = () => {
  if (!navigator.clipboard || (window as any).__sprigClipboardPatched) {
    return;
  }
  (window as any).__sprigClipboardPatched = true;

  const originalWriteText = navigator.clipboard.writeText?.bind(
    navigator.clipboard,
  );
  if (originalWriteText) {
    navigator.clipboard.writeText = (text: string): Promise<void> =>
      originalWriteText(text).catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          // Clipboard access not available in this context (e.g. Safari iframe).
          // This is expected and non-actionable — silently swallow.
          return;
        }
        throw err;
      });
  }

  const originalWrite = navigator.clipboard.write?.bind(navigator.clipboard);
  if (originalWrite) {
    navigator.clipboard.write = (data: ClipboardItems): Promise<void> =>
      originalWrite(data).catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          return;
        }
        throw err;
      });
  }
};

const app = (config: AppConfig<'SprigV2'>) => {
  // Logic needed to notify client of change in height so that the iframe can be resized accordingly.
  function forwardSurveyHeight(_payload: unknown) {
    // Necessary because Sprig is not explicit in the param types for their listeners
    const payload: SprigSurveyHeightPayload =
      _payload as SprigSurveyHeightPayload;
    if (typeof payload?.contentFrameHeight === 'number') {
      config.send({
        type: 'sprigSurveyHeight',
        value: payload.contentFrameHeight,
      });
    }
  }

  const forwardSurveyWidth = (event: unknown) => {
    const contentFrameWidth = (event as any)?.contentFrameWidth;
    if (typeof contentFrameWidth === 'number') {
      config.send({
        type: 'sprigSurveyWidth',
        value: contentFrameWidth,
      });
    }
  };

  // Track survey presentation for Sprig targeting and notify the client that a survey is ready to display.
  function forwardSurveyWillPresent(payload: unknown) {
    if (isSprigSurveyWillPresentPayload(payload)) {
      Sprig?.track(`seen-${payload['survey.id']}`);
    }

    config.send({type: 'sprigSurveyWillPresent'});
  }

  // Logic needed to notify client that a Sprig survey has been closed (completed / dismissed).
  function forwardSurveyWillClose(_payload: unknown) {
    // Necessary because Sprig is not explicit in the param types for their listeners
    const payload: SprigSurveyWillClosePayload =
      _payload as SprigSurveyWillClosePayload;

    if (payload?.initiator === 'survey.completed') {
      config.send({type: 'sprigSurveyCompleted'});
    } else {
      config.send({type: 'sprigSurveyClosed'});
    }
  }

  // Logic needed to notify client that the Sprig SDK has loaded (SDK loads asynchronously).
  function forwardSDKReady() {
    config.send({type: 'sprigSDKReady'});
  }

  // Logic to monitor / handle incoming messages from the client.
  config.subscribe((messageData) => {
    switch (messageData.type) {
      case 'initialize':
        const extractedStyleNonce = extractCSPNonce('style');
        const extractedScriptNonce = extractCSPNonce('script');

        // Patch clipboard API before Sprig SDK initializes to prevent
        // NotAllowedError in Safari's sandboxed iframe context.
        patchClipboardForSafari();

        const {sprigApiURL} = messageData;
        const normalizedSprigApiURL = sprigApiURL
          ? normalizeSprigApiURL(sprigApiURL)
          : undefined;

        Sprig = SprigSDK.configure({
          environmentId: messageData.environmentId,
          styleNonce: extractedStyleNonce,
          nonce: extractedScriptNonce,
          ...(normalizedSprigApiURL ? {_API_URL: normalizedSprigApiURL} : {}),
        });

        Sprig?.setUserId(messageData.userId);
        if (Sprig) {
          Sprig.maxHeight = '100vh';
        }

        Sprig?.addListener(SprigEvent.SurveyHeight, forwardSurveyHeight);
        Sprig?.addListener(SprigEvent.SurveyWidth, forwardSurveyWidth);
        Sprig?.addListener(
          SprigEvent.SurveyWillPresent,
          forwardSurveyWillPresent,
        );
        Sprig?.addListener(SprigEvent.SurveyWillClose, forwardSurveyWillClose);
        Sprig?.addListener(SprigEvent.SDKReady, forwardSDKReady);

        if (messageData.css) {
          Sprig?.applyStyles(messageData.css);
        }

        break;
      case 'logout':
        Sprig?.logoutUser();
        break;
      case 'setAttribute':
        Sprig?.setAttribute(messageData.attribute, messageData.value);
        break;
      case 'setAttributes':
        Sprig?.setAttributes(messageData.attributes);
        break;
      case 'setUserId':
        Sprig?.setUserId(messageData.userId);
        break;
      case 'removeAttributes':
        Sprig?.removeAttributes(messageData.attributes);
        break;
      case 'removeLocalStorageItems':
        storage.remove('userleap.ids');
        storage.remove('userleap.pageviews');
        break;
      case 'dismissSurvey':
        Sprig?.dismissActiveSurvey();
        break;
      case 'teardown':
        Sprig?.teardown();
        break;
      case 'track': {
        const {event, properties = {}, metadata = {}} = messageData;

        if (shouldTrackEvent(event, metadata)) {
          Sprig?.track(event, properties, metadata);
        }

        break;
      }
      default:
        // eslint-disable-next-line no-console
        console.error('Unhandled incoming payload: ', messageData);
    }
  });
};

initialize({
  app,
  owner: 'dashboard_discovery',
});
